import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import FilterBar from '../../components/Post/FilterBar.tsx';
import DetailedPane from '../../components/Comments/DetailedPane.tsx';
import DeleteConfirmation from '../../components/Comments/DeleteConfirmation.tsx';
import useComments from '../../hooks/useComments.tsx';
import PaginationControls from '../../utils/pagination';
import CommentsTable from '../../components/Comments/CommentsTable.tsx';
import { useSocial } from '../../context/socialProvider.tsx';
import { useNavigate, useLocation } from 'react-router-dom';

interface BaseCommentsProps {
  title: string;
}

const ROW_HEIGHT = 57; // px, adjust to your actual row height

const BaseComments: React.FC<BaseCommentsProps> = ({ title }) => {
  const [autoMod, setAutoMod] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isAdCommentsPage = location.pathname.includes('adComments');
  const [adComments, setAdComments] = useState(isAdCommentsPage);

  const {
    comments,
    totalComments,
    selectedComment,
    currentPage,
    totalPages,
    sortOrder,
    statusFilter,
    hideReplies,
    isLoading,
    isDeleting,
    setComments,
    setSelectedComment,
    setCurrentPage,
    setSortOrder,
    setStatusFilter,
    setHideReplies,
    removeComment,
    updateCommentHiddenStatus,
    replyToComment,
    searchText,
    setSearchText,
    removal,
    setRemoval,
    setCommentsPerPage,
    commentsPerPage,
  } = useComments({ autoMod, adComments });

  const {
    currentSocial,
    user,
    isLoading: isPlatformLoading,
    error: platformError,
  } = useSocial();

  const navigate = useNavigate();

  const [isDeleteConfirmationActive, setIsDeleteConfirmationActive] =
    useState(false);

  const deleteWindow = () => {
    setIsDeleteConfirmationActive(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const clickEvent = (index: number) => {
    setSelectedComment(index);
  };

  return (
    <>
      <div
        className="h-[calc(100vh-var(--header-height,0px))] flex flex-col overflow-hidden"
        ref={(el) => {
          if (el) {
            // Calculate available height by measuring header offset
            const headerHeight = el.getBoundingClientRect().top;
            el.style.setProperty('--header-height', `${headerHeight}px`);
          }
        }}
      >
        <h1 className="ml-4">{title}</h1>
        <div className="bg-grey flex divide-x divide-white divide-opacity-20 flex-1">
          <div
            className={`flex flex-col w-9/12 ${
              isDeleteConfirmationActive && 'pointer-events-none'
            }`}
            ref={tableContainerRef}
          >
            <FilterBar
              sortOrder={sortOrder}
              setSortOrder={(value: any) => {
                setSortOrder(value);
                setCurrentPage(1);
              }}
              statusFilter={statusFilter}
              setStatusFilter={(value: any) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              hideReplies={hideReplies}
              setHideReplies={(value: any) => {
                setHideReplies(value);
                setCurrentPage(1);
              }}
              searchText={searchText}
              setSearchText={setSearchText}
              removal={removal}
              setRemoval={setRemoval}
              autoMod={autoMod}
              setAutoMod={setAutoMod}
            />
            <CommentsTable
              comments={comments}
              selectedComment={selectedComment}
              setSelectedComment={setSelectedComment}
              isLoading={isLoading}
              totalComments={totalComments}
              clickEvent={clickEvent}
              activePlatform={currentSocial}
              commentsPerPage={commentsPerPage}
            />
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          </div>
          <div className={`relative overflow-hidden w-5/12`}>
            <div
              className={`${
                isDeleteConfirmationActive && 'blur-sm pointer-events-none'
              }`}
            >
              <DetailedPane
                currentSocial={currentSocial}
                comments={comments}
                setComments={setComments}
                selectedComment={selectedComment}
                setSelectedComment={setSelectedComment}
                deleteWindow={deleteWindow}
                isLoading={isLoading}
                updateCommentHiddenStatus={updateCommentHiddenStatus}
                replyToComment={replyToComment}
                autoMod={autoMod}
              />
            </div>
            {isDeleteConfirmationActive && (
              <DeleteConfirmation
                cancel={setIsDeleteConfirmationActive}
                removeComment={removeComment}
                isDeleting={isDeleting}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BaseComments;
